// src/components/QuickEditModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import CategoryAutocomplete from './CategoryAutocomplete';
import StoreAutocomplete from './StoreAutocomplete';

const QuickEditModal = ({
  isOpen,
  onClose,
  field,
  currentValue,
  itemId,
  itemName,
  onSaveSuccess,
  showToast,
  availableCategories = [],
  availableLists = [],
  context = 'category' // 'category', 'list', 'rename', 'price', 'note', 'newList', 'taxRate', 'budget'
}) => {
  const [value, setValue] = useState('');
  const [selectedList, setSelectedList] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);

  // Determinar tipo de input
  const isTextarea = field === 'note';
  const isPrice = field === 'price';
  const isRename = field === 'rename';
  const isCategory = field === 'category';
  const isList = field === 'list' || context === 'list';
  const isNewList = context === 'newList';
  const isTaxRate = context === 'taxRate';
  const isBudget = context === 'budget';

  const getFieldLabel = () => {
    if (isNewList) return 'Create New List';
    if (isTaxRate) return 'Edit Tax Rate';
    if (isBudget) return 'Edit Budget';
    switch (field) {
      case 'rename': return 'Rename Item';
      case 'price': return 'Change Price';
      case 'category': return 'Change Category';
      case 'list': return 'Select List';
      case 'note': return 'Edit Note';
      default: return 'Edit';
    }
  };

  const getPlaceholder = () => {
    if (isNewList) return 'Enter list name...';
    if (isTaxRate) return 'Enter tax rate (0-100)...';
    if (isBudget) return 'Enter budget amount...';
    if (isList) return 'Select list...';
    if (isPrice) return 'Enter new price...';
    if (isRename) return 'Enter new name...';
    if (isCategory) return 'Select or type category...';
    if (isTextarea) return 'Enter note...';
    return '';
  };

  // Resetear estado cuando se abre/cierra
  useEffect(() => {
    if (isOpen) {
      // Para price y budget: valor vacío o currentValue según el caso
      if (isPrice || isBudget) {
        setValue(currentValue !== undefined && currentValue !== '' ? currentValue : '');
      } else if (isTaxRate) {
        setValue(currentValue !== undefined && currentValue !== '' ? currentValue : '');
      } else {
        setValue(currentValue || '');
      }
      setSelectedList(null);
      setIsSaving(false);
      
      // Auto-focus después de un breve delay
      setTimeout(() => {
        if ((isCategory || isList || isNewList || isTaxRate || isBudget) && inputRef.current) {
          inputRef.current.focus();
          if (isTaxRate || isBudget || isPrice) {
            inputRef.current.select();
          }
        } else if (isTextarea && textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.select();
        } else if (inputRef.current && !isCategory && !isList && !isTaxRate && !isBudget) {
          inputRef.current.focus();
          if (isPrice) {
            inputRef.current.select();
          }
        }
      }, 50);
    }
  }, [isOpen, currentValue, isPrice, field, isCategory, isList, isNewList, isTextarea, isTaxRate, isBudget]);

  const handleClear = () => {
    setValue('');
    if (inputRef.current && !isCategory && !isList && !isTaxRate && !isBudget) {
      inputRef.current.focus();
    }
  };

  const handleSave = async () => {
    if (isSaving) return;

    let trimmedValue = value;
    
    // Validaciones específicas por campo
    if (isPrice) {
      const numValue = parseFloat(trimmedValue);
      if (trimmedValue && (isNaN(numValue) || numValue < 0)) {
        showToast('Please enter a valid price', 'error');
        return;
      }
      trimmedValue = trimmedValue ? parseFloat(trimmedValue) || 0 : 0;
    } else if (isTaxRate) {
      const numValue = parseFloat(trimmedValue);
      if (trimmedValue === '' || trimmedValue === undefined) {
        showToast('Please enter a tax rate', 'error');
        return;
      }
      if (isNaN(numValue) || numValue < 0 || numValue > 100) {
        showToast('Please enter a valid tax rate (0-100)', 'error');
        return;
      }
      trimmedValue = numValue;
    } else if (isBudget) {
      const numValue = parseFloat(trimmedValue);
      if (trimmedValue === '' || trimmedValue === undefined) {
        showToast('Please enter a budget amount', 'error');
        return;
      }
      if (isNaN(numValue) || numValue < 0) {
        showToast('Please enter a valid budget amount', 'error');
        return;
      }
      trimmedValue = numValue;
    } else if ((isRename || isNewList) && !trimmedValue.trim()) {
      showToast(isNewList ? 'List name cannot be empty' : 'Item name cannot be empty', 'error');
      return;
    } else if (isList && !trimmedValue.trim()) {
      showToast('Please select a list', 'error');
      return;
    } else if (isCategory && !trimmedValue.trim()) {
      showToast('Please enter or select a category', 'error');
      return;
    }

    setIsSaving(true);

    try {
      // Determinar qué campo actualizar
      let updateData = {};
      
      if (isNewList) {
        updateData = { newList: trimmedValue.trim() };
      } else if (isPrice) {
        updateData = { price: trimmedValue };
      } else if (isTaxRate) {
        updateData = { taxRate: trimmedValue };
      } else if (isBudget) {
        updateData = { budget: trimmedValue };
      } else if (isRename) {
        updateData = { item: trimmedValue.trim() };
      } else if (isCategory) {
        updateData = { category: trimmedValue.trim() };
      } else if (isList) {
        updateData = selectedList
          ? { list_id: selectedList.id, list_name: selectedList.name }
          : { list_name: trimmedValue.trim() };
      } else if (isTextarea) {
        updateData = { note: trimmedValue.trim() };
      }

      // Mensaje de éxito
      const successMessage = isNewList ? 'New list created' :
                            isTaxRate ? 'Tax rate updated' :
                            isBudget ? 'Budget updated' :
                            isPrice ? 'Price updated' : 
                            isRename ? 'Name updated' : 
                            isCategory ? 'Category updated' : 
                            isList ? 'List updated' : 
                            'Note updated';
      
      await onSaveSuccess(updateData);
      onClose();
      if (showToast && typeof showToast === 'function') {
        showToast(successMessage, 'success');
      }
      
    } catch (error) {
      if (showToast && typeof showToast === 'function') {
        showToast(`Error: ${error.message}`, 'error');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isTextarea) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleListChange = (listOrValue) => {
    if (typeof listOrValue === 'object' && listOrValue !== null) {
      setSelectedList(listOrValue);
      setValue(listOrValue.name);
      return;
    }

    setSelectedList(null);
    setValue(listOrValue);
  };

  const handleCategoryChange = (category) => {
    setValue(category);
  };

  // Determinar si el botón Save debe estar habilitado
  const isSaveDisabled = () => {
    if (isSaving) return true;
    
    if ((isRename || isNewList || isList || isCategory) && !value.trim()) return true;
    
    // Para taxRate y budget, requerir valor numérico válido
    if (isTaxRate || isBudget) {
      const numValue = parseFloat(value);
      if (value === '' || value === undefined || isNaN(numValue)) return true;
      if (isTaxRate && (numValue < 0 || numValue > 100)) return true;
      if (isBudget && numValue < 0) return true;
    }
    
    return false;
  };

  // Si no está abierto, no renderizar nada
  if (!isOpen) return null;

  // Unidad para mostrar junto al input
  const getUnitSuffix = () => {
    if (isTaxRate) return '%';
    if (isPrice || isBudget) return '$';
    return '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4 z-50">
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                {getFieldLabel()}
              </h2>
            </div>
            <button
              onClick={onClose}
              disabled={isSaving}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none disabled:opacity-50"
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        </div>

        <div className="px-6 py-4">
          <div className="mb-6">
            {isTextarea ? (
              <div className="relative">
                <textarea
                  ref={textareaRef}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder={getPlaceholder()}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
                {value && (
                  <button
                    onClick={handleClear}
                    className="absolute right-3 top-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                    type="button"
                    aria-label="Clear"
                  >
                    <i className="fas fa-times text-gray-600"></i>
                  </button>
                )}
              </div>
            ) : isCategory ? (
              <div className="relative">
                <CategoryAutocomplete
                  ref={inputRef}
                  value={value}
                  onChange={handleCategoryChange}
                  categories={availableCategories}
                  placeholder={getPlaceholder()}
                  disabled={isSaving}
                  isLoading={isSaving}
                  dropdownPosition="bottom"
                />
              </div>
            ) : isList || isNewList ? (
              <div className="relative">
                {isNewList ? (
                  <input
                    ref={inputRef}
                    type="text"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder={getPlaceholder()}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-12"
                  />
                ) : (
                  <StoreAutocomplete
                    ref={inputRef}
                    value={value}
                    onChange={handleListChange}
                    lists={availableLists}
                    placeholder={getPlaceholder()}
                    disabled={isSaving}
                    isLoading={isSaving}
                    dropdownPosition="bottom"
                  />
                )}
                {value && (
                  <button
                    onClick={handleClear}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                    type="button"
                    aria-label="Clear"
                  >
                    <i className="fas fa-times text-gray-600"></i>
                  </button>
                )}
              </div>
            ) : (
              <div className="relative">
                <input
                  ref={inputRef}
                  type={isPrice || isTaxRate || isBudget ? "number" : "text"}
                  step={isPrice || isBudget ? "0.01" : isTaxRate ? "0.1" : undefined}
                  min={isPrice || isBudget ? "0" : isTaxRate ? "0" : undefined}
                  max={isTaxRate ? "100" : undefined}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder={getPlaceholder()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-12"
                />
                {value && (
                  <button
                    onClick={handleClear}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                    type="button"
                    aria-label="Clear"
                  >
                    <i className="fas fa-times text-gray-600"></i>
                  </button>
                )}
                {/* Unidad (%, $) */}
                {getUnitSuffix() && (
                  <span className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                    {getUnitSuffix()}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaveDisabled()}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                isSaveDisabled()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickEditModal;